import { useState, useEffect } from 'react';
import { Server, Database, HardDrive, Activity, TrendingUp, AlertTriangle, RefreshCw, BarChart3, Zap, Users, Globe, Clock, DollarSign } from 'lucide-react';
import { tenantConfigurationService } from '../../../services/tenantConfigurationService';

export default function ResourceAllocation({ organization, onUpdate }) {
  const [resourceData, setResourceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [optimizationSuggestions, setOptimizationSuggestions] = useState([]);

  useEffect(() => {
    loadResourceData();
  }, [organization?.id]);

  const loadResourceData = async () => {
    try {
      setLoading(true);
      const data = await tenantConfigurationService?.getResourceAllocation(organization?.id);
      setResourceData(data);
      generateOptimizationSuggestions(data);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const generateOptimizationSuggestions = (data) => {
    const suggestions = [];
    
    // Storage optimization
    const storageUsage = data?.resourceMetrics?.storageUsed || 0;
    if (storageUsage > 8000) { // 8GB threshold
      suggestions?.push({
        type: 'storage',
        severity: 'high',
        title: 'High Storage Usage',
        description: 'Consider upgrading storage plan or cleaning up unused files',
        action: 'Upgrade Plan'
      });
    }

    // API usage optimization
    const apiCalls = data?.resourceMetrics?.apiCalls || 0;
    if (apiCalls > 50000) { // 50K threshold
      suggestions?.push({
        type: 'api',
        severity: 'medium',
        title: 'High API Usage',
        description: 'Consider optimizing API calls or upgrading to higher limits',
        action: 'Optimize Usage'
      });
    }

    // User scaling
    if (data?.memberCount > 20 && data?.subscription?.planName === 'starter') {
      suggestions?.push({
        type: 'users',
        severity: 'medium',
        title: 'User Limit Approaching',
        description: 'Consider upgrading to Professional plan for more users',
        action: 'Upgrade Plan'
      });
    }

    // Cost optimization
    if (data?.subscription?.status === 'active' && data?.memberCount < 3) {
      suggestions?.push({
        type: 'cost',
        severity: 'low',
        title: 'Potential Cost Savings',
        description: 'Low user count detected. Consider downgrading to save costs',
        action: 'Review Plan'
      });
    }

    setOptimizationSuggestions(suggestions);
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000)?.toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000)?.toFixed(1) + 'K';
    }
    return num?.toString();
  };

  const getUsagePercentage = (used, limit) => {
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'text-red-600 bg-red-50 border-red-200';
    if (percentage >= 75) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-green-600 bg-green-50 border-green-200';
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading resource allocation...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Resource Allocation</h2>
          <p className="text-gray-600">
            Monitor and optimize resource usage for {organization?.name}
          </p>
        </div>
        <button
          onClick={loadResourceData}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Refresh</span>
        </button>
      </div>
      {/* Resource Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Storage Used</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatBytes((resourceData?.resourceMetrics?.storageUsed || 2400) * 1024 * 1024)}
              </p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <HardDrive className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full" 
                style={{ width: `${getUsagePercentage(resourceData?.resourceMetrics?.storageUsed || 2400, 10000)}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {getUsagePercentage(resourceData?.resourceMetrics?.storageUsed || 2400, 10000)?.toFixed(1)}% of 10GB limit
            </p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">API Calls</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(resourceData?.resourceMetrics?.apiCalls || 12500)}
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <Activity className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-600 h-2 rounded-full" 
                style={{ width: `${getUsagePercentage(resourceData?.resourceMetrics?.apiCalls || 12500, 100000)}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {getUsagePercentage(resourceData?.resourceMetrics?.apiCalls || 12500, 100000)?.toFixed(1)}% of 100K limit
            </p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">DB Connections</p>
              <p className="text-2xl font-bold text-gray-900">
                {resourceData?.resourceMetrics?.databaseConnections || 15}
              </p>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <Database className="h-6 w-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-purple-600 h-2 rounded-full" 
                style={{ width: `${getUsagePercentage(resourceData?.resourceMetrics?.databaseConnections || 15, 50)}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {getUsagePercentage(resourceData?.resourceMetrics?.databaseConnections || 15, 50)?.toFixed(1)}% of 50 limit
            </p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Users</p>
              <p className="text-2xl font-bold text-gray-900">
                {resourceData?.memberCount || 0}
              </p>
            </div>
            <div className="bg-orange-100 p-3 rounded-lg">
              <Users className="h-6 w-6 text-orange-600" />
            </div>
          </div>
          <div className="flex items-center mt-4 text-sm">
            <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
            <span className="text-green-600">+2 this month</span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Usage Breakdown */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage Breakdown</h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <Server className="h-5 w-5 text-blue-600 mr-3" />
                <div>
                  <p className="font-medium text-gray-900">Compute Usage</p>
                  <p className="text-sm text-gray-600">CPU hours this month</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-bold text-gray-900">847h</p>
                <p className="text-sm text-green-600">↑ 12%</p>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <Globe className="h-5 w-5 text-green-600 mr-3" />
                <div>
                  <p className="font-medium text-gray-900">Bandwidth</p>
                  <p className="text-sm text-gray-600">Data transfer this month</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-bold text-gray-900">156 GB</p>
                <p className="text-sm text-red-600">↓ 5%</p>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <Clock className="h-5 w-5 text-purple-600 mr-3" />
                <div>
                  <p className="font-medium text-gray-900">Active Time</p>
                  <p className="text-sm text-gray-600">User session hours</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-bold text-gray-900">1,234h</p>
                <p className="text-sm text-green-600">↑ 8%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription & Billing */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription Details</h3>
          
          {resourceData?.subscription ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">
                    {resourceData?.subscription?.planName?.charAt(0)?.toUpperCase() + resourceData?.subscription?.planName?.slice(1)} Plan
                  </p>
                  <p className="text-sm text-gray-600">
                    Status: <span className={`capitalize font-medium ${
                      resourceData?.subscription?.status === 'active' ? 'text-green-600' : 'text-yellow-600'
                    }`}>
                      {resourceData?.subscription?.status}
                    </span>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">
                    ${(resourceData?.subscription?.priceAmount / 100)?.toFixed(2)}
                  </p>
                  <p className="text-sm text-gray-600">per month</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <DollarSign className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">This Month</p>
                  <p className="font-bold text-gray-900">
                    ${(resourceData?.subscription?.priceAmount / 100)?.toFixed(2)}
                  </p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <BarChart3 className="h-6 w-6 text-green-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Next Billing</p>
                  <p className="font-bold text-gray-900">
                    {new Date(resourceData.subscription.currentPeriodEnd)?.toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No active subscription</p>
            </div>
          )}
        </div>
      </div>
      {/* AI-Powered Optimization Suggestions */}
      {optimizationSuggestions?.length > 0 && (
        <div className="mt-8 bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Zap className="h-5 w-5 text-blue-600 mr-2" />
            AI-Powered Optimization Suggestions
          </h3>
          
          <div className="space-y-4">
            {optimizationSuggestions?.map((suggestion, index) => (
              <div key={index} className={`border rounded-lg p-4 ${getSeverityColor(suggestion?.severity)}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <h4 className="font-medium">{suggestion?.title}</h4>
                      <span className={`ml-2 px-2 py-1 text-xs rounded-full border ${getSeverityColor(suggestion?.severity)}`}>
                        {suggestion?.severity?.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm mt-1 opacity-80">{suggestion?.description}</p>
                  </div>
                  <button className="ml-4 px-3 py-1 bg-white bg-opacity-50 rounded text-sm font-medium hover:bg-opacity-70 transition-colors">
                    {suggestion?.action}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Recent Activity */}
      <div className="mt-8 bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Resource Activity</h3>
        
        <div className="space-y-3">
          {resourceData?.recentActivity?.slice(0, 5)?.map((activity, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <Activity className="h-4 w-4 text-gray-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{activity?.action}</p>
                  <p className="text-xs text-gray-600">
                    {activity?.user?.fullName} • {new Date(activity.createdAt)?.toLocaleString()}
                  </p>
                </div>
              </div>
              <span className="text-xs text-gray-500">{activity?.entityType}</span>
            </div>
          )) || (
            <div className="text-center py-8 text-gray-500">
              <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No recent activity</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}